import { useState, useEffect, useMemo } from 'react'
import { Star, ThumbsUp, CheckCircle, X, Upload, Filter } from 'lucide-react'
import { useReviewStore } from '../../stores/review'
import { useAuthStore } from '../../stores/auth'
import { useOrderStore } from '../../stores/order'
import { generateMockReviews } from '../../mock/seeds/reviews'
import { format } from 'date-fns'
import type { ReviewSortOption } from '../../types/review'

interface ProductReviewsProps {
  productId: string
  productTitle: string
}

export default function ProductReviews({ productId, productTitle }: ProductReviewsProps) {
  const { addReview, getProductReviews, toggleHelpful, sortReviews } = useReviewStore()
  const { user, isAuthenticated } = useAuthStore()
  const { orders } = useOrderStore()
  
  const [showWriteReview, setShowWriteReview] = useState(false)
  const [rating, setRating] = useState(5)
  const [hoveredRating, setHoveredRating] = useState(0)
  const [title, setTitle] = useState('')
  const [comment, setComment] = useState('')
  const [images, setImages] = useState<string[]>([])
  const [sortBy, setSortBy] = useState<ReviewSortOption>('most_helpful')
  const [ratingFilter, setRatingFilter] = useState<number | 'all'>('all')
  
  // Initialize with mock reviews if empty
  useEffect(() => {
    const productReviews = getProductReviews(productId)
    if (productReviews.length === 0) {
      const mockReviews = generateMockReviews(productId, 15)
      mockReviews.forEach((review) => {
        addReview({
          product_id: review.product_id,
          user_id: review.user_id,
          user_name: review.user_name,
          user_avatar: review.user_avatar,
          rating: review.rating,
          title: review.title,
          comment: review.comment,
          images: review.images,
          is_verified_purchase: review.is_verified_purchase,
        })
      })
    }
  }, [productId, getProductReviews, addReview])

  // Check if user has purchased this product
  const hasPurchased = useMemo(() => {
    if (!isAuthenticated || !user) return false
    return orders.some(
      (order) => 
        order.status === 'delivered' &&
        order.items.some((item) => item.product_id === productId)
    )
  }, [orders, productId, isAuthenticated, user])

  // Get product reviews
  const productReviews = getProductReviews(productId)

  // Filter and sort reviews
  const filteredAndSortedReviews = useMemo(() => {
    let filtered = productReviews
    if (ratingFilter !== 'all') {
      filtered = filtered.filter((r) => r.rating === ratingFilter)
    }
    return sortReviews(filtered, sortBy)
  }, [productReviews, sortBy, ratingFilter, sortReviews])

  // Calculate stats
  const stats = useMemo(() => {
    const total = productReviews.length
    if (total === 0) {
      return {
        average: 0,
        total: 0,
        distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      }
    }

    const sum = productReviews.reduce((acc, r) => acc + r.rating, 0)
    const distribution = {
      5: productReviews.filter((r) => r.rating === 5).length,
      4: productReviews.filter((r) => r.rating === 4).length,
      3: productReviews.filter((r) => r.rating === 3).length,
      2: productReviews.filter((r) => r.rating === 2).length,
      1: productReviews.filter((r) => r.rating === 1).length,
    }

    return {
      average: sum / total,
      total,
      distribution,
    }
  }, [productReviews])

  const handleSubmitReview = (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    addReview({
      product_id: productId,
      user_id: user.id,
      user_name: user.name,
      user_avatar: undefined,
      rating,
      title,
      comment,
      images,
      is_verified_purchase: hasPurchased,
    })

    // Reset form
    setRating(5)
    setTitle('')
    setComment('')
    setImages([])
    setShowWriteReview(false)
    alert('✓ Review submitted successfully!')
  }

  const handleImageUpload = () => {
    // In a real app, this would upload images
    // For now, we'll just use placeholder URLs
    const mockImageUrl = `https://source.unsplash.com/400x400/?grocery&sig=${Date.now()}`
    setImages([...images, mockImageUrl])
  }

  const handleToggleHelpful = (reviewId: string) => {
    if (!user) {
      alert('Please log in to mark reviews as helpful')
      return
    }
    toggleHelpful(reviewId, user.id)
  }

  return (
    <div className="space-y-8">
      {/* Reviews Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Customer Reviews</h2>
        {isAuthenticated && hasPurchased && (
          <button
            onClick={() => setShowWriteReview(!showWriteReview)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {showWriteReview ? 'Cancel' : 'Write a Review'}
          </button>
        )}
      </div>

      {/* Write Review Form */}
      {showWriteReview && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Write Your Review</h3>
          <form onSubmit={handleSubmitReview} className="space-y-4">
            {/* Rating */}
            <div>
              <label className="block text-sm font-medium mb-2">Rating *</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="p-1"
                  >
                    <Star
                      className={`w-8 h-8 ${
                        star <= (hoveredRating || rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-2">Review Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Summarize your experience"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Comment */}
            <div>
              <label className="block text-sm font-medium mb-2">Review *</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your thoughts about this product..."
                required
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium mb-2">Add Photos (Optional)</label>
              <div className="flex gap-4 items-center">
                {images.map((img, idx) => (
                  <div key={idx} className="relative">
                    <img src={img} alt="" className="w-20 h-20 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => setImages(images.filter((_, i) => i !== idx))}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {images.length < 3 && (
                  <button
                    type="button"
                    onClick={handleImageUpload}
                    className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <Upload className="w-6 h-6 text-gray-400" />
                  </button>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Submit Review
            </button>
          </form>
        </div>
      )}

      {/* Review Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 border border-gray-200 rounded-lg p-6">
        {/* Average Rating */}
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-5xl font-bold mb-2">{stats.average.toFixed(1)}</div>
            <div className="flex items-center justify-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-5 h-5 ${
                    star <= Math.round(stats.average)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <div className="text-sm text-gray-600">{stats.total} reviews</div>
          </div>
        </div>

        {/* Rating Distribution */}
        <div className="space-y-2">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = stats.distribution[star as 5 | 4 | 3 | 2 | 1]
            const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0

            return (
              <button
                key={star}
                onClick={() => setRatingFilter(ratingFilter === star ? 'all' : star)}
                className={`w-full flex items-center gap-3 text-sm hover:bg-white transition-colors rounded px-2 py-1 ${
                  ratingFilter === star ? 'bg-white ring-2 ring-blue-500' : ''
                }`}
              >
                <span className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium">{star}</span>
                </span>
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-yellow-400 h-2 rounded-full"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="text-gray-600 w-12 text-right">{count}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Filters and Sort */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium">
            {ratingFilter === 'all' ? 'All Reviews' : `${ratingFilter} Star Reviews`}
          </span>
          {ratingFilter !== 'all' && (
            <button
              onClick={() => setRatingFilter('all')}
              className="text-sm text-blue-600 hover:underline"
            >
              Clear
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as ReviewSortOption)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="most_helpful">Most Helpful</option>
            <option value="most_recent">Most Recent</option>
            <option value="highest_rating">Highest Rating</option>
            <option value="lowest_rating">Lowest Rating</option>
          </select>
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-6">
        {filteredAndSortedReviews.length === 0 ? (
          <div className="text-center py-12">
            <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">
              {ratingFilter !== 'all' 
                ? `No ${ratingFilter} star reviews yet` 
                : 'No reviews yet. Be the first to review!'}
            </p>
          </div>
        ) : (
          filteredAndSortedReviews.map((review) => (
            <div key={review.id} className="border border-gray-200 rounded-lg p-6">
              {/* Review Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {review.user_avatar ? (
                    <img
                      src={review.user_avatar}
                      alt={review.user_name}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">
                        {review.user_name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{review.user_name}</span>
                      {review.is_verified_purchase && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          Verified Purchase
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {format(new Date(review.created_at), 'MMM dd, yyyy')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${
                      star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>

              {/* Title and Comment */}
              <h4 className="font-semibold mb-2">{review.title}</h4>
              <p className="text-gray-700 mb-4">{review.comment}</p>

              {/* Images */}
              {review.images.length > 0 && (
                <div className="flex gap-2 mb-4">
                  {review.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt=""
                      className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                    />
                  ))}
                </div>
              )}

              {/* Helpful Button */}
              <button
                onClick={() => handleToggleHelpful(review.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  user && review.helpful_voters.includes(user.id)
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ThumbsUp className="w-4 h-4" />
                <span className="text-sm">
                  Helpful {review.helpful_count > 0 && `(${review.helpful_count})`}
                </span>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
